import { format, startOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';

import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

import { INPUT_DATE_PICKER_ICON_POSITION } from './input-date-picker.types';
import type { InputDatePickerProps } from './input-date-picker.types';

const parseInputDate = (value: string) => {
  const dateOnlyParts = value.split('-');
  if (dateOnlyParts.length === 3) {
    return new Date(
      Number(dateOnlyParts[0]),
      Number(dateOnlyParts[1]) - 1,
      Number(dateOnlyParts[2]),
    );
  }
  return undefined;
};

const toInputDateValue = (date: Date) => format(date, 'yyyy-MM-dd');

export function InputDatePicker({
  allowClear = true,
  align = 'start',
  ariaLabel,
  className,
  defaultValue,
  disablePast = false,
  displayFormat = 'PPP',
  iconPosition = INPUT_DATE_PICKER_ICON_POSITION.START,
  id,
  onChange,
  placeholder = 'Pick a date',
  value,
}: InputDatePickerProps) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseInputDate(value);
  const defaultDate = parseInputDate(defaultValue ?? '');
  const minSelectableDate = disablePast ? startOfDay(new Date()) : undefined;
  const calendarDefaultMonth = selectedDate ?? defaultDate ?? minSelectableDate;
  const calendarKeySource = value !== '' ? value : (defaultValue ?? '');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          aria-label={ariaLabel}
          className={cn(
            'w-full text-left font-normal',
            iconPosition === INPUT_DATE_PICKER_ICON_POSITION.END
              ? 'justify-between'
              : 'justify-start',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          {iconPosition === INPUT_DATE_PICKER_ICON_POSITION.START ? (
            <CalendarIcon className='mr-2 size-4 shrink-0' />
          ) : null}
          <span className='min-w-0 truncate'>
            {selectedDate ? format(selectedDate, displayFormat) : placeholder}
          </span>
          {iconPosition === INPUT_DATE_PICKER_ICON_POSITION.END ? (
            <CalendarIcon className='ml-2 size-4 shrink-0' />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={12}
        className='w-auto max-w-[calc(100vw-1.5rem)] p-0'
      >
        <Calendar
          key={`${open ? 'open' : 'closed'}-${calendarKeySource}`}
          mode='single'
          selected={selectedDate}
          defaultMonth={calendarDefaultMonth}
          disabled={
            minSelectableDate ? { before: minSelectableDate } : undefined
          }
          onSelect={(date) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive guard for library callback
            if (!date) return;
            onChange(toInputDateValue(date));
            setOpen(false);
          }}
          required
        />
        {allowClear && value ? (
          <div className='border-t p-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='w-full'
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear date
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
